import {tokens} from '@pleo-io/telescope'
import {Tabs, TabList, Tab, TabPanels, TabPanel} from '@reach/tabs'
import {m} from 'framer-motion'
import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import Image from '@helios/shared/components/image'
import {Video} from '@helios/shared/components/video'
import {Section, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import ImageSource from '@helios/shared/types/image'
import InlineVideo from '@helios/shared/types/inline-video'

import * as tracking from '../tracking'

const variants = {
    active: {
        opacity: 1,
        x: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'block',
        transition: {
            type: 'tween',
            duration: 0.53,
        },
    },
    inactive: {
        opacity: 0,
        x: -200,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute' as const,
        transition: {
            type: 'tween',
            duration: 0.53,
        },
        transitionEnd: {
            display: 'none',
        },
    },
}

interface TabProps {
    icon: ImageSource
    title: string
    description: string
    poster: ImageSource
    video: InlineVideo
}

interface Props {
    tabs: TabProps[]
}

export const VideoTabs = ({tabs}: Props) => {
    const [activeIndex, setActiveIndex] = React.useState(0)
    const wrapperRef = React.useRef<HTMLElement>(null)
    const {trackFirstInteraction} = tracking.useHomepageTracking(wrapperRef, 'video_tabs_section')

    const moveToNextTab = () => {
        setActiveIndex((activeIndex) => (activeIndex < tabs.length - 1 ? activeIndex + 1 : 0))
    }

    const handleTabClick = (index: number) => {
        trackFirstInteraction('video_tabs_interacted')
        setActiveIndex(index)
    }

    return (
        <Section ref={wrapperRef}>
            <Layout
                variant="two-asymmetrical"
                mirrored
                alignItems="center"
                as={Tabs}
                index={activeIndex}
                onChange={handleTabClick}
                orientation="vertical"
            >
                <StyledTabList>
                    {tabs.map((tab, i) => (
                        <StyledTab key={`${tab.title}-${i}`}>
                            <TabUpper>
                                {i === activeIndex && (
                                    <Image image={tab.icon} width={35} aria-hidden />
                                )}

                                <Text
                                    as="span"
                                    variant="title-base"
                                    color={i === activeIndex ? 'default' : 'lightMuted'}
                                >
                                    {tab.title}
                                </Text>
                            </TabUpper>

                            {i === activeIndex && (
                                <Text variant="paragraph-large" color="muted">
                                    {tab.description}
                                </Text>
                            )}
                        </StyledTab>
                    ))}
                </StyledTabList>

                <TabPanels style={{position: 'relative'}}>
                    {tabs.map((tab, i) => (
                        <TabPanel
                            key={i}
                            as={m.div}
                            variants={variants}
                            animate={i === activeIndex ? 'active' : 'inactive'}
                        >
                            <VideoPlayer
                                moveToNextTab={moveToNextTab}
                                video={tab.video}
                                poster={tab.poster}
                            />
                        </TabPanel>
                    ))}
                </TabPanels>
            </Layout>
        </Section>
    )
}

interface VideoPlayerProps {
    poster: ImageSource | null
    video: InlineVideo
    moveToNextTab: () => void
}

const VideoPlayer = ({video, poster, moveToNextTab}: VideoPlayerProps) => {
    const videoRef = React.useRef<HTMLVideoElement>(null)
    const [progress, setProgress] = React.useState(0)
    const [videoContainerRef, inView] = useInView({
        threshold: 0.1,
    })

    React.useEffect(() => {
        const node = videoRef.current

        if (!node) {
            return
        }

        const interval = setInterval(() => {
            setProgress((node.currentTime / node.duration) * 100)
        }, 15)

        if (inView) {
            node.play()
        } else {
            node.pause()
            node.currentTime = 0
        }

        return () => {
            clearInterval(interval)
        }
    }, [inView])

    return (
        <div ref={videoContainerRef}>
            <ProgressBar value={progress} max={100} />

            <VideoSquare>
                <Video
                    onEnded={() => moveToNextTab()}
                    ref={videoRef}
                    poster={poster?.url}
                    video={video}
                    posterWidth={600}
                    loop={false}
                />
            </VideoSquare>
        </div>
    )
}

const StyledTabList = styled(TabList)`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: ${tokens.spacing60};
`

const StyledTab = styled(Tab)`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: ${tokens.spacing12};
    align-content: start;
    padding: 0;
    text-align: left;
    cursor: pointer;
    background: none;
    border: none;
`

const TabUpper = styled.span`
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    column-gap: ${tokens.spacing12};
`

const VideoSquare = styled.div`
    aspect-ratio: 1 / 1;
    overflow: hidden;
`

const ProgressBar = styled.progress`
    display: block;
    width: 100%;
    height: 4px;
    background: ${tokens.shade300};
    border: none;
    appearance: none;

    ::-webkit-progress-bar {
        background-color: ${tokens.shade300};
    }

    ::-moz-progress-bar {
        background-color: ${tokens.shade600};
    }

    ::-webkit-progress-value {
        background-color: ${tokens.shade600};
    }
`
